import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useUserRoles } from '@features/user';
import { LightbulbOutlined } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import type { PulseAssignmentRequest, PulseAssignmentResponse } from '../store/pulseApi';
import {
  useAssignPulseMutation,
  usePulseAssignmentsQuery,
  usePulseOptionsQuery,
} from '../store/pulseApi';

const roles = [
  'practice_administrator',
  'clinician',
  'clinical_staff',
  'operations_support',
  'billing_administrator',
  'supervisor',
] satisfies NonNullable<PulseAssignmentRequest['role']>[];

const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const label = (value: string) => value.replaceAll('_', ' ');

function errorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'data' in error) {
    const data = error.data;
    if (
      typeof data === 'object' &&
      data !== null &&
      'message' in data &&
      typeof data.message === 'string'
    )
      return data.message;
  }
  return 'Request failed. Check the API connection and your admin access, then retry.';
}

export function PulseAdmin({ initialOrganizationId = '' }: { initialOrganizationId?: string }) {
  const [organizationId, setOrganizationId] = useState(initialOrganizationId);
  const [userId, setUserId] = useState('');
  const [role, setRole] = useState<NonNullable<PulseAssignmentRequest['role']> | ''>('');
  const [pulseCode, setPulseCode] =
    useState<PulseAssignmentRequest['pulse_code']>('jotrx_prescriber_setup');
  const [ignoreCooldown, setIgnoreCooldown] = useState(false);
  const [assignmentId, setAssignmentId] = useState(() => crypto.randomUUID());
  const [result, setResult] = useState<PulseAssignmentResponse>();
  const [error, setError] = useState('');
  const [historyOffset, setHistoryOffset] = useState(0);
  const options = usePulseOptionsQuery();
  const [assign, { isLoading }] = useAssignPulseMutation();
  const validOrg = uuidPattern.test(organizationId.trim());
  const history = usePulseAssignmentsQuery(
    {
      organizationId: organizationId.trim(),
      userId: userId.trim() || undefined,
      offset: historyOffset,
      limit: 20,
    },
    { skip: !validOrg },
  );
  const selected = options.data?.pulses.find(pulse => pulse.pulse_code === pulseCode);
  const ready = result?.recipients.filter(recipient => recipient.status === 'ready').length ?? 0;

  const reset = () => {
    setResult(undefined);
    setError('');
    setAssignmentId(crypto.randomUUID());
    setHistoryOffset(0);
  };
  const submit = async (dryRun: boolean) => {
    setError('');
    const request: PulseAssignmentRequest = {
      assignment_id: assignmentId,
      organization_id: organizationId.trim(),
      pulse_code: pulseCode,
      user_id: userId.trim() || null,
      role: role || null,
      ignore_cooldown: ignoreCooldown,
      dry_run: dryRun,
      expected_user_ids: dryRun ? null : result?.recipients.map(recipient => recipient.user_id),
    };
    try {
      const response = await assign({ pulseAssignmentRequest: request }).unwrap();
      setResult(response);
      if (!dryRun) void history.refetch();
    } catch (caught) {
      setError(errorMessage(caught));
    }
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', p: 3 }}>
      <Stack direction="row" spacing={1} sx={{ mb: 1, alignItems: 'center' }}>
        <LightbulbOutlined color="primary" />
        <Typography variant="h4">Pulses</Typography>
      </Stack>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Send a guide suggestion through Jot to selected members of a practice.
      </Typography>
      {options.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Pulse options could not load. This API may not have manual assignments deployed yet.
        </Alert>
      )}
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Typography variant="h6">Choose a Pulse and audience</Typography>
          <TextField
            select
            label="Pulse"
            value={pulseCode}
            disabled={isLoading || options.isLoading}
            onChange={event => {
              const option = options.data?.pulses.find(
                pulse => pulse.pulse_code === event.target.value,
              );
              if (option) {
                setPulseCode(option.pulse_code);
                reset();
              }
            }}
          >
            {(options.data?.pulses ?? []).map(pulse => (
              <MenuItem key={pulse.pulse_code} value={pulse.pulse_code}>
                {pulse.article.title}
              </MenuItem>
            ))}
          </TextField>
          {selected && (
            <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1 }}>
              <Typography>{selected.prompt}</Typography>
              <Typography variant="caption" color="text.secondary">
                {selected.article.slug}
              </Typography>
            </Box>
          )}
          <TextField
            label="Organization ID"
            value={organizationId}
            disabled={isLoading}
            onChange={event => {
              setOrganizationId(event.target.value);
              reset();
            }}
            helperText="Required. Assignment stays within this organization."
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              fullWidth
              label="User ID (optional)"
              value={userId}
              disabled={isLoading}
              onChange={event => {
                setUserId(event.target.value);
                reset();
              }}
              helperText="Leave blank to include all matching members."
            />
            <TextField
              fullWidth
              select
              label="Organization role"
              slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
              value={role}
              disabled={isLoading}
              onChange={event => {
                setRole(roles.find(candidate => candidate === event.target.value) ?? '');
                reset();
              }}
              helperText="When a user is specified, they must also hold this role."
            >
              <MenuItem value="">All roles</MenuItem>
              {roles.map(value => (
                <MenuItem key={value} value={value}>
                  {label(value)}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <FormControlLabel
            control={
              <Checkbox
                checked={ignoreCooldown}
                disabled={isLoading}
                onChange={event => {
                  setIgnoreCooldown(event.target.checked);
                  reset();
                }}
              />
            }
            label="Ignore cooldown for this assignment (repeat testing)"
          />
          <Typography variant="body2" color="text.secondary">
            Manual assignment skips prescribing-signal eligibility. Jot and Pulse flags must still
            be enabled, and the member must be active on desktop. New members are not added
            automatically.
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              disabled={!validOrg || !selected || isLoading}
              onClick={() => void submit(true)}
            >
              {isLoading ? 'Working…' : 'Preview recipients'}
            </Button>
            <Button
              variant="contained"
              disabled={!result?.dry_run || ready === 0 || isLoading}
              onClick={() => void submit(false)}
            >
              Assign to {ready} {ready === 1 ? 'member' : 'members'}
            </Button>
            {result && !result.dry_run && (
              <Button disabled={isLoading} onClick={reset}>
                New assignment
              </Button>
            )}
          </Stack>
          {result && (
            <>
              <Alert severity={result.dry_run ? 'info' : 'success'}>
                {result.dry_run
                  ? `${result.recipients.length} matching members. ${ready} ready to receive this Pulse.`
                  : `${result.recipients.filter(recipient => recipient.status === 'queued').length} queued. Delivery occurs on an active heartbeat; queued does not mean shown.`}
              </Alert>
              <Table size="small" aria-label="Assignment recipients">
                <TableHead>
                  <TableRow>
                    <TableCell>Member</TableCell>
                    <TableCell>Result</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.recipients.map(recipient => (
                    <TableRow key={recipient.user_id}>
                      <TableCell>{recipient.user_id}</TableCell>
                      <TableCell>
                        <Chip size="small" label={label(recipient.status)} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </Stack>
      </Paper>
      <Stack
        direction="row"
        sx={{ mt: 4, mb: 1, alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Typography variant="h6">Manual assignment history</Typography>
        <Button disabled={!validOrg || history.isFetching} onClick={() => void history.refetch()}>
          Refresh history
        </Button>
      </Stack>
      {!validOrg ? (
        <Typography color="text.secondary">
          Enter an organization ID to see its assignments.
        </Typography>
      ) : history.isFetching && !history.currentData ? (
        <Typography color="text.secondary">Loading assignment history…</Typography>
      ) : history.error ? (
        <Alert severity="error">Assignment history could not load.</Alert>
      ) : (
        <>
          <Table size="small" aria-label="Assignment history">
            <TableHead>
              <TableRow>
                <TableCell>Member</TableCell>
                <TableCell>Pulse</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Assigned by</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(history.currentData?.assignments ?? []).map(row => (
                <TableRow key={row.nudge_id}>
                  <TableCell>{row.user_id}</TableCell>
                  <TableCell>{label(row.pulse_code)}</TableCell>
                  <TableCell>
                    {row.cancelled_at
                      ? `Cancelled: ${row.cancel_reason}`
                      : row.consumed_at
                        ? 'Served'
                        : 'Pending'}
                  </TableCell>
                  <TableCell>
                    {row.assigned_by}
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      {new Date(row.created_at).toLocaleString()}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {!history.isFetching && history.currentData?.assignments.length === 0 && (
            <Typography sx={{ py: 2 }} color="text.secondary">
              No manual assignments.
            </Typography>
          )}
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button
              disabled={historyOffset === 0 || history.isFetching}
              onClick={() => setHistoryOffset(Math.max(0, historyOffset - 20))}
            >
              Previous
            </Button>
            <Button
              disabled={history.currentData?.next_offset == null || history.isFetching}
              onClick={() => {
                if (history.currentData?.next_offset != null)
                  setHistoryOffset(history.currentData.next_offset);
              }}
            >
              Next
            </Button>
          </Stack>
        </>
      )}
    </Box>
  );
}

export default function PulsesPage() {
  const userRoles = useUserRoles();
  const [params] = useSearchParams();
  if (!userRoles.includes('Admin'))
    return <Alert severity="error">Admin access is required to manage Pulses.</Alert>;
  return <PulseAdmin initialOrganizationId={params.get('organization_id') ?? ''} />;
}
